import { useAction, useSubmission } from '@solidjs/router';
import { Form, Input, TextField, Label, Button, Errors, TextLink, FieldDescription } from '@troon/ui';
import { createEffect, createSignal, createUniqueId, onMount, Show } from 'solid-js';
import { useTrackEvent } from '@troon/analytics';
import { clientAction } from '@troon/api-client';
import { withScope } from '@sentry/solidstart';
import { createTurnstile } from '@troon/turnstile';
import { useAuthStore } from '../flow';
import { getConfigValue } from '../../../modules/config';

export function UserExists(props: { nextNewUser: string; nextReturningUser: string }) {
	const [store, setStore] = useAuthStore();
	const [input, setInput] = createSignal<HTMLInputElement>();
	const trackEvent = useTrackEvent();
	const [isValid, setIsValid] = createSignal(
		/^[^@]+@[^@]+\.[^@]+$/.test(store.data.email ?? '') || /^\d{5,}$/.test(store.data.email ?? ''),
	);
	const formId = createUniqueId();
	const submission = useSubmission(userExistsAction, ([data]) => data.get('__formId') === formId);
	const [formRef, setFormRef] = createSignal<HTMLFormElement>();
	const handleSubmit = useAction(userExistsAction);
	const [turnstile, setTurnstile] = createTurnstile({
		siteKey: getConfigValue('TURNSTILE_SITE_KEY'),
		name: 'header.x-token',
		onError: (error, tries) => {
			withScope((scope) => {
				scope.captureMessage(`Turnstile Error: ${error}`, 'warning', { data: tries });
			});
		},
		onSubmit: () => handleSubmit(new FormData(formRef())),
	});
	const [rewardsIdError, setRewardsIdError] = createSignal<string>();

	createEffect(() => {
		if (submission.error) {
			setTurnstile({ loaded: false, waiting: false, response: undefined });
			window.turnstile.reset(turnstile.ref);
		}
	});

	createEffect(() => {
		if (submission.result) {
			switch (submission.result?.status) {
				case 'NO_ACCOUNT':
					if (/^\d+$/.test(store.data.email ?? '')) {
						setRewardsIdError('The account ID entered appears to be invalid.');
						break;
					}
					trackEvent('signupEnteredEmail', { email: store.data.email });
					setStore({ flow: 'register', step: props.nextNewUser });
					break;
				case 'EXISTS_AND_FIRST_LOGIN':
					trackEvent('signupEnteredEmail', { email: store.data.email });
					setStore({ flow: 'firstLogin', step: '/auth/first-login' });
					break;
				default:
					trackEvent('loginEnteredEmail', { email: store.data.email });
					setStore({ flow: 'login', step: props.nextReturningUser });
					break;
			}

			submission.clear();
		}
	});

	createEffect(() => {
		if (submission.pending) {
			setRewardsIdError(undefined);
		}
	});

	onMount(() => {
		if (input() && store.data.email) {
			input()!.value = store.data.email;
		}
	});

	return (
		<Form
			action={userExistsAction}
			suppressRequired
			id={formId}
			ref={setFormRef}
			onsubmit={(e) => {
				if (import.meta.env.NODE_ENV !== 'test') {
					if (!turnstile.response && !turnstile.waiting) {
						e.preventDefault();
						setTurnstile('waiting', true);
						return false;
					}
				}
				return true;
			}}
		>
			<TextField name="email">
				<Label>Email address</Label>
				<Input
					autofocus
					ref={setInput}
					inputMode="email"
					autocomplete="username email"
					onInput={(e) => {
						const value = e.currentTarget.value;
						setStore('data', 'email', value);
						setIsValid(/^[^@]+@[^@]+\.[^@]+$/.test(value) || /^\d{5,}$/.test(value));
					}}
				/>
				<FieldDescription>If you were previously a Troon Rewards account holder, use that email.</FieldDescription>
			</TextField>

			<div class="hidden" aria-hidden>
				<TextField name="password">
					<Label>Password</Label>
					<Input type="password" onInput={(e) => setStore('data', 'password', e.target.value)} tabindex={-1} />
				</TextField>
			</div>

			<Errors />

			<Show when={turnstile.error ?? rewardsIdError()}>
				{(error) => (
					<p class="text-red-500" aria-live="assertive">
						{error()}
					</p>
				)}
			</Show>

			<div class="flex flex-col">
				<div data-refresh-expired={submission.error ? 'manual' : 'auto'} ref={(el) => setTurnstile('ref', el)} />
				<Button disabled={!isValid()} pending={turnstile.waiting && !submission.result && !submission.error}>
					Continue
				</Button>
			</div>

			<p class="text-center text-sm sm:mx-12">
				By clicking “continue” you are agreeing to Troon’s <TextLink href="/terms">Terms of Service</TextLink> and{' '}
				<TextLink href="/privacy-policy">Privacy Policy</TextLink>.
			</p>
		</Form>
	);
}

const userExistsAction = clientAction('get', '/v0/users/exists');
